import React, { useState } from 'react';
import { 
  Typography, 
  TextField, 
  Button, 
  Card, 
  Alert, 
  AlertTitle,
  Box,
  Container,
  Grid,
  IconButton,
  Divider
} from '@mui/material';
import { useNavigate, Link } from 'react-router-dom';
import supabaseClient from '../supabaseClient';
import styled from '@emotion/styled';
import {
  Facebook,
  Twitter,
  LinkedIn,
  Instagram,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';
import logo0 from '../assets/logo0.png';
import backgroundImage from '../assets/backgroundlogin.png';
import yellowBackground from '../assets/yellow_background.png';

const LoginCard = styled(Card)({
  maxWidth: '500px',
  width: '100%',
  padding: '40px',
  backgroundColor: '#fff',
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
  borderRadius: '4px',
});

const StyledTextField = styled(TextField)({
  marginBottom: '24px',
  '& .MuiOutlinedInput-root': {
    height: '48px',
    '& fieldset': {
      borderColor: '#e0e0e0',
    },
    '&:hover fieldset': {
      borderColor: '#007C3F',
    },
  },
  '& .MuiInputLabel-root': {
    color: '#666',
    transform: 'translate(14px, 14px) scale(1)',
  },
});

const SignUpButton = styled(Button)({
  backgroundColor: '#007C3F',
  color: '#fff',
  padding: '12px',
  fontSize: '16px',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: '#006F3C',
  },
});

const HelpLink = styled(Link)({
  color: '#007C3F',
  textDecoration: 'none',
  fontSize: '14px',
  '&:hover': {
    textDecoration: 'underline',
  },
});

// Add new styled components for header and footer
const StyledHeader = styled(Box)({
  backgroundColor: '#fff',
  borderBottom: '1px solid #e0e0e0',
  padding: '16px 0',
  marginBottom: '32px',
});

const StyledFooter = styled(Box)({
  backgroundColor: '#333333',
  color: '#fff',
  padding: '48px 0 24px',
  marginTop: '64px',
});

const SocialButton = styled(IconButton)({
  color: '#fff',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
});

const FooterLink = styled(Link)({
  color: '#fff',
  textDecoration: 'none',
  fontSize: '14px',
  display: 'block',
  marginBottom: '8px',
  '&:hover': {
    textDecoration: 'underline',
  },
});

const FooterHeading = styled(Typography)({
  color: '#fff',
  fontSize: '16px',
  fontWeight: 500,
  marginBottom: '16px',
});

const Join = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  async function handleSubmit(e) {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Add email confirmation options
      const { data: authData, error: signUpError } = await supabaseClient.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          emailRedirectTo: `${window.location.origin}/login`, // Add redirect URL
          data: {
            first_name: formData.firstName,
            last_name: formData.lastName,
          }
        }
      });

      // Add better error handling and logging
      if (signUpError) {
        console.error('Signup error details:', signUpError);
        throw signUpError;
      }

      // Log the response to check if email confirmation was triggered
      console.log('Signup response:', authData);

      if (authData && authData.user) {
        // 2. Create profile in profiles table
        const { error: profileError } = await supabaseClient
          .from('profiles')
          .upsert({
            id: authData.user.id,
            email: formData.email,
            first_name: formData.firstName,
            last_name: formData.lastName,
            username: formData.email.split('@')[0],
            full_name: `${formData.firstName} ${formData.lastName}`,
            updated_at: new Date().toISOString()
          }, {
            onConflict: 'id'
          });

        if (profileError) {
          console.error('Profile creation error:', profileError);
          throw profileError;
        }

        setSuccess(true);
        setFormData({
          email: '',
          password: '',
          confirmPassword: '',
          firstName: '',
          lastName: '',
        });
      } else {
        setError('No user data received from signup');
      }
    } catch (error) {
      console.error('Detailed error:', error);
      setError(error.message || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Box sx={{ 
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      width: '100%',
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: `url(${yellowBackground})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        opacity: 0.8,
        zIndex: -1
      }
    }}>
      {/* Header */}
      <Box 
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          padding: '20px',
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#000000'
        }}
      >
        <Box 
          sx={{
            maxWidth: '800px',
            width: '100%',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <Link to="/" style={{ textDecoration: 'none' }}>
            <img 
              src={logo0} 
              alt="Domain Factory" 
              style={{ 
                height: '30px',
                filter: 'brightness(0) invert(1)'
              }}
            />
          </Link>
        </Box>
      </Box>

      <Box sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        pt: 4,
        pb: 4,
        backgroundColor: 'transparent'
      }}>
        <LoginCard>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {success ? (
            <Alert 
              severity="success" 
              sx={{ 
                mb: 3,
                '& .MuiAlert-message': {
                  width: '100%'
                }
              }}
            >
              <AlertTitle>Account Created Successfully!</AlertTitle>
              <Typography variant="body1" paragraph>
                A verification email has been sent to {formData.email}.
              </Typography>
              <Typography variant="body2">
                Please check your email and click the verification link to activate your account.
              </Typography>
              <Button
                onClick={() => navigate('/login')}
                sx={{ 
                  mt: 2,
                  color: '#007C3F',
                  textTransform: 'none'
                }}
              >
                Go to Login
              </Button>
            </Alert>
          ) : (
            <>
              <Typography variant="h5" sx={{ mb: 4, textAlign: 'center', fontWeight: 500 }}>
                Create Your Account
              </Typography>

              <form onSubmit={handleSubmit}>
                <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                  <div>
                    <Typography variant="body1" sx={{ mb: 1, color: '#333', fontWeight: 500 }}>
                      First Name
                    </Typography>
                    <StyledTextField
                      fullWidth
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div>
                    <Typography variant="body1" sx={{ mb: 1, color: '#333', fontWeight: 500 }}>
                      Last Name
                    </Typography>
                    <StyledTextField
                      fullWidth
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </Box>

                <Typography variant="body1" sx={{ mb: 1, color: '#333', fontWeight: 500 }}>
                  Email Address
                </Typography>
                <StyledTextField
                  fullWidth
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />

                <Typography variant="body1" sx={{ mb: 1, color: '#333', fontWeight: 500 }}>
                  Password
                </Typography>
                <StyledTextField
                  fullWidth
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                />

                <Typography variant="body1" sx={{ mb: 1, color: '#333', fontWeight: 500 }}>
                  Confirm Password
                </Typography>
                <StyledTextField
                  fullWidth
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                />

                <SignUpButton
                  type="submit"
                  fullWidth
                  disabled={loading}
                >
                  Create Account
                </SignUpButton>
              </form>
            </>
          )}

          {!success && (
            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: '#666' }}>
                Already have an account?{' '}
                <HelpLink to="/login">
                  Sign in here
                </HelpLink>
              </Typography>
            </Box>
          )}
        </LoginCard>
      </Box>

      {/* Footer */}
      <StyledFooter>
        <Container maxWidth="xl">
          <Grid container spacing={4}>
            <Grid item xs={12} md={3}>
              <FooterHeading>Produkte</FooterHeading>
              <FooterLink to="/domains">Domains</FooterLink>
              <FooterLink to="/homepage">Homepage</FooterLink>
              <FooterLink to="/webhosting">Webhosting</FooterLink>
              <FooterLink to="/webhosting-plus">Webhosting Plus</FooterLink>
              <FooterLink to="/email-essentials">E-Mail Essentials</FooterLink>
              <FooterLink to="/managed-cloud">Managed Cloud Server</FooterLink>
              <FooterLink to="/ssl">SSL-Zertifikate</FooterLink>
              <FooterLink to="/preise">Preise</FooterLink>
            </Grid>

            <Grid item xs={12} md={3}>
              <FooterHeading>Unternehmen</FooterHeading>
              <FooterLink to="/uber-uns">Über uns</FooterLink>
              <FooterLink to="/jobs">Jobs</FooterLink>
              <FooterLink to="/referenzen">Referenzen</FooterLink>
              <FooterLink to="/service">Service</FooterLink>
              <FooterLink to="/partnerprogramm">Partnerprogramm</FooterLink>
              <FooterLink to="/freunde-werben">Freunde werben</FooterLink>
            </Grid>

            <Grid item xs={12} md={3}>
              <FooterHeading>Rechtliches</FooterHeading>
              <FooterLink to="/impressum">Impressum</FooterLink>
              <FooterLink to="/datenschutz">Datenschutz</FooterLink>
              <FooterLink to="/cookies">Cookies</FooterLink>
              <FooterLink to="/widerrufsrecht">Widerrufsrecht</FooterLink>
              <FooterLink to="/kundigen">Jetzt kündigen</FooterLink>
              <FooterLink to="/agb">AGB</FooterLink>
              <FooterLink to="/digital-services">Digital Services Act</FooterLink>
              <FooterLink to="/offenlegung">Offenlegung</FooterLink>
            </Grid>

            <Grid item xs={12} md={3}>
              <FooterHeading>Support</FooterHeading>
              <FooterLink to="/formulare">Formulare</FooterLink>
              <FooterLink to="/faq">FAQ</FooterLink>
              <FooterLink to="/statusmeldung">Statusmeldung</FooterLink>
              <FooterLink to="/downloads">Downloads</FooterLink>
              <FooterLink to="/kontakt">Kontakt</FooterLink>
              <Typography variant="body2" sx={{ color: '#fff', mt: 2 }}>
                089 998 288 026
              </Typography>
              <Typography variant="body2" sx={{ color: '#fff' }}>
                support@df.eu
              </Typography>
            </Grid>
          </Grid>

          <Box sx={{ 
            mt: 4, 
            pt: 2, 
            borderTop: '1px solid rgba(255,255,255,0.1)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Box>
              <Typography variant="body2" sx={{ color: '#fff', opacity: 0.7 }}>
                © {new Date().getFullYear()} Domain Factory
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <IconButton size="small" sx={{ color: '#fff' }}>
                <Facebook fontSize="small" />
              </IconButton>
              <IconButton size="small" sx={{ color: '#fff' }}>
                <Twitter fontSize="small" />
              </IconButton>
              <IconButton size="small" sx={{ color: '#fff' }}>
                <LinkedIn fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </Container>
      </StyledFooter>
    </Box>
  );
};

export default Join;
